import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, MapPin, Calendar, Mail, ExternalLink, ChevronLeft, TrainFront, TramFront, Bus, Hourglass, ArrowRight, Info, BookOpen, Box } from 'lucide-react';
import KuutioCubeGradient from '../components/KuutioCubeGradient';
import { useTranslation } from '../hooks/useTranslation';
import API_ENDPOINTS from '../config/api';
import PreWorkList from '../components/Me/PreWorkList';
import HomeworkList from '../components/Me/HomeworkList';
import MeSignIn from '../components/Me/MeSignIn';
import MeDatasets from '../components/Me/MeDatasets';
import UserMenu from '../components/UserMenu';
import {
  Page, Column, Eyebrow, Title, CUBE, Head, HeadRow, HeadBody,
  TitleRow, Tags, TopBar, Dot, Tag, Transit, TransitRow, Card,
  CardButton, Row, SectionTitle, Muted, Dl, MapBox, MapFrame, Enter,
  Facilitate, Goal, Countdown, NoteItem, NoteList, Link, TabBar, Tab,
  Back,
} from './MeView.styles';

const TAB_PATH = { prework: 'ennakkotehtavat', homework: 'kotitehtavat' };
const TRANSIT_ICON = { subway: TrainFront, train: TrainFront, tram: TramFront, bus: Bus };
const MAX_LINES = 12;

/** A small box around the venue — tight enough to place the door, wide enough
 *  to show which street you come in from. */
function osmEmbed(lat, lon) {
  const pad = 0.0035;
  const bbox = [lon - pad, lat - pad / 2, lon + pad, lat + pad / 2].map(n => n.toFixed(5)).join(',');
  // OSM's own marker: the frame is interactive, so a marker has to move with
  // the map, and only the embed's can.
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(5)},${lon.toFixed(5)}`;
}

/** Lines within walking distance, per mode — the same block under a Lyyti venue and the Kuutio room. */
function TransitLines({ transit }) {
  const { t } = useTranslation();
  if (!transit?.length) return null;
  return (
    <Transit>
      {transit.map((tr) => {
        const Icon = TRANSIT_ICON[tr.mode] || Bus;
        const shown = tr.lines.slice(0, MAX_LINES).join(', ');
        const more = tr.lines.length - MAX_LINES;
        return (
          <TransitRow key={tr.mode}>
            <Icon size={15} />
            <span>
              <strong>{t(`me.transit.${tr.mode}`)}</strong> {shown}{more > 0 ? ` ${t('me.transit.more', { count: more })}` : ''}
              {tr.stop && <Muted as="span"> · {tr.stop}{tr.distanceM != null ? `, ${tr.distanceM} m` : ''}</Muted>}
            </span>
          </TransitRow>
        );
      })}
    </Transit>
  );
}

/** The venue on a map, when its coordinates are known. */
function VenueMap({ place }) {
  const { t } = useTranslation();
  if (place?.lat == null || place?.lon == null) return null;
  return (
    <MapBox>
      <MapFrame
        src={osmEmbed(place.lat, place.lon)}
        title={`${place.location || ''} ${place.address || ''}`.trim() || t('me.where')}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </MapBox>
  );
}

/** The organiser's notes arrive as plain text with bare URLs in them. */
function linkify(text) {
  return text.split(/(https?:\/\/\S+)/g).map((part, i) => (
    /^https?:\/\//.test(part)
      ? <Link key={i} href={part} target="_blank" rel="noreferrer">{part.replace(/^https?:\/\//, '')}</Link>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

/**
 * The organiser's date string — "7.–8.9.2026", "11.8.2026" — as a start and
 * an end. The last full date is the end; a leading bare day ("7.–") is the
 * start of that same month.
 */
function datesFrom(text) {
  const full = [...String(text || '').matchAll(/(\d{1,2})\.(\d{1,2})\.(\d{4})/g)];
  if (!full.length) return null;
  const [, ed, em, ey] = full[full.length - 1];
  const end = new Date(Number(ey), Number(em) - 1, Number(ed), 23, 59, 59);
  const lead = String(text).match(/^\s*(\d{1,2})\.\s*[–-]/);
  const start = lead
    ? new Date(Number(ey), Number(em) - 1, Number(lead[1]))
    : new Date(Number(full[0][3]), Number(full[0][2]) - 1, Number(full[0][1]));
  return { start: start.getTime(), end: end.getTime() };
}

/**
 * Upcoming, ongoing or past. Lyyti's dates decide when there are any; the
 * organiser's own date string next; the instance status only when neither
 * exists — a finished workshop is often still at 'running' in the database.
 */
function phaseOf(w) {
  const now = Date.now();
  let span = null;
  if (w.event?.startsAt) {
    const start = new Date(w.event.startsAt).getTime();
    span = { start, end: w.event.endsAt ? new Date(w.event.endsAt).getTime() : start + 8 * 3600 * 1000 };
  } else {
    span = datesFrom(w.workshopDate);
  }
  if (span) {
    if (now > span.end) return 'past';
    if (now >= span.start) return 'ongoing';
    return 'upcoming';
  }
  if (['archived', 'post-workshop', 'completed'].includes(w.status)) return 'past';
  if (/^day\d/.test(w.status || '')) return 'ongoing';
  return 'upcoming';
}

/** The pills a workshop carries: ONLINE when it is one, then its phase. */
function PhaseTags({ w, t }) {
  return (
    <Tags>
      {w.instanceType === 'online' && <Tag $phase="online">{t('me.phase.online')}</Tag>}
      <Tag $phase={phaseOf(w)}>{t(`me.phase.${phaseOf(w)}`)}</Tag>
    </Tags>
  );
}

/** Days and hours until the doors open; hours and minutes on the last day. */
function countdownTo(startsAt, now) {
  const ms = new Date(startsAt).getTime() - now;
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return { days, hours, minutes: minutes % 60 };
}

/** A workshop spans two days more often than not, so a single range reads better. */
function formatWhen(event, locale) {
  if (!event?.startsAt) return null;
  const opts = { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' };
  const start = new Date(event.startsAt);
  const end = event.endsAt ? new Date(event.endsAt) : null;
  const f = (d) => new Intl.DateTimeFormat(locale, { ...opts, timeZone: event.timezone || 'Europe/Helsinki' }).format(d);
  return end ? `${f(start)} – ${f(end)}` : f(start);
}

/**
 * MeView — /me, a participant's own Kuutio.
 *
 * The only screen in Kuutio written for attendees rather than staff, so it says
 * what a person needs the week before: when, where, and what the organiser has
 * on file about them. Corrections are a link to their own Lyyti page — Kuutio
 * reads the registration system and never writes to it.
 */
const MeView = ({ join = false } = {}) => {
  const { t, language } = useTranslation();
  const [state, setState] = useState({ status: 'loading' });
  // /me/<slug> opens one workshop; /me lists them. The slug is the report's
  // (K8-kuutio-8-vero), so a workshop has one name in every URL.
  const { slug, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  // The tab lives in the URL (/me/<slug>/ennakkotehtavat) so a refresh keeps it.
  const tab = tabParam === TAB_PATH.prework ? 'prework' : tabParam === TAB_PATH.homework ? 'homework' : 'practical';
  const setTab = (next) => {
    const w = workshopsRef.current.find((x) => x.id === openId);
    if (!w?.slug) return;
    navigate(next === 'practical' ? `/me/${w.slug}` : `/me/${w.slug}/${TAB_PATH[next]}`);
  };
  const workshopsRef = useRef([]);
  // The tab's dot: the server's answer on load, PreWorkList's after each task.
  const [todo, setTodo] = useState({});
  const setTodoFor = useCallback((id, value) => setTodo((prev) => (prev[id] === value ? prev : { ...prev, [id]: value })), []);
  const [homeworkTodo, setHomeworkTodo] = useState({});
  const setHomeworkTodoFor = useCallback((id, value) => setHomeworkTodo((prev) => (prev[id] === value ? prev : { ...prev, [id]: value })), []);
  // The countdown moves on its own; once a minute is enough for days and hours.
  const [entering, setEntering] = useState(false);
  const [enterError, setEnterError] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // The join link: /liity/<slug>?k=<code>. The workshop's name is fetched so
  // the door can say what the person is joining; the code travels with the
  // sign-in request and is judged there.
  const [searchParams] = useSearchParams();
  const [joinInfo, setJoinInfo] = useState(join ? { slug, code: searchParams.get('k') || '', title: null, hasJoinCode: null } : null);
  useEffect(() => {
    if (!join || !slug) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.ME_JOIN(slug)).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!cancelled && d) setJoinInfo((j) => ({ ...j, title: d.title, hasJoinCode: d.hasJoinCode, slug: d.slug || j.slug }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [join, slug]);

  const load = useCallback(() => {
    fetch(API_ENDPOINTS.ME_WORKSHOPS(), { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 401) return setState({ status: 'anonymous' });
        if (!res.ok) return setState({ status: 'error' });
        const data = await res.json();
        setState({ status: 'ready', ...data });
        workshopsRef.current = data.workshops || [];
        setTodo(Object.fromEntries((data.workshops || []).map((w) => [w.id, !!w.preWorkTodo])));
        setHomeworkTodo(Object.fromEntries((data.workshops || []).map((w) => [w.id, !!w.homeworkTodo])));
        const wanted = slug && !join && data.workshops?.find((w) => w.slug?.toLowerCase() === slug.toLowerCase());
        if (wanted) setOpenId(wanted.id);
        // One workshop is the common case; do not make someone choose from a list of one.
        else if (data.workshops?.length === 1) setOpenId(data.workshops[0].id);
        // A workshop with pre-work waiting is the reason the person came:
        // open it on the task, so the first thing they see is the question,
        // not a list (21.9.2026: colleagues signed in and stopped at the list).
        else {
          const due = data.workshops?.find((w) => w.preWorkTodo);
          if (due?.slug) { setOpenId(due.id); navigate(`/me/${due.slug}/${TAB_PATH.prework}`, { replace: true }); }
        }
      })
      .catch(() => setState({ status: 'error' }));
  }, [slug, join]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  // Signed in at the door: leave the join link behind and load the page.
  const onSignedIn = useCallback(() => {
    if (join) navigate(joinInfo?.slug ? `/me/${joinInfo.slug}` : '/me', { replace: true });
    setState({ status: 'loading' });
    load();
  }, [join, joinInfo?.slug, navigate, load]);

  if (state.status === 'loading') {
    return <Page><Column><Muted>{t('me.loading')}</Muted></Column></Page>;
  }

  if (state.status === 'anonymous') {
    return (
      <Page><Column>
        <div><Eyebrow>Kuutio</Eyebrow><Title>{t('me.heading')}</Title></div>
        <Muted>{t('me.signInPrompt')}</Muted>
        <MeSignIn join={joinInfo} initialEmail={searchParams.get('e') || ''} onSignedIn={onSignedIn} />
      </Column></Page>
    );
  }

  if (state.status === 'error') {
    return <Page><Column><Muted>{t('me.failed')}</Muted></Column></Page>;
  }

  const workshops = state.workshops || [];
  const open = workshops.find(w => w.id === openId) || null;

  if (!workshops.length) {
    return (
      <Page><Column>
        <TopBar>
          <div><Eyebrow>Kuutio</Eyebrow><Title>{t('me.heading')}</Title></div>
          <UserMenu user={state.user} />
        </TopBar>
        <MeDatasets empty={<Muted>{t('me.none')}</Muted>} />
      </Column></Page>
    );
  }

  if (!open) {
    return (
      <Page><Column>
        <TopBar>
          <div><Eyebrow>{state.user?.name || state.user?.email}</Eyebrow><Title>{t('me.heading')}</Title></div>
          <UserMenu user={state.user} />
        </TopBar>
        <Muted>{state.viewingAsStaff ? t('me.staffView') : t('me.chooseWorkshop')}</Muted>
        {workshops.map(w => (
          <CardButton key={w.id} onClick={() => { setOpenId(w.id); if (w.slug) navigate(`/me/${w.slug}`); }}>
            <TitleRow>
              <strong>{w.title}</strong>
              <PhaseTags w={w} t={t} />
            </TitleRow>
            <Muted>{formatWhen(w.event, language) || t('me.noEventInfo')}</Muted>
          </CardButton>
        ))}
        <MeDatasets />
      </Column></Page>
    );
  }

  const { event, you } = open;
  const when = formatWhen(event, language);

  return (
    <Page><Column>
      <TopBar>
        {workshops.length > 1
          ? <Back onClick={() => { setOpenId(null); navigate('/me'); }}><ChevronLeft size={16} /> {t('me.back')}</Back>
          : <span />}
        <UserMenu user={state.user} />
      </TopBar>

      <Head>
        {/* The cube sits level with the title, not the whole ingress. */}
        <HeadRow>
          <KuutioCubeGradient size={CUBE} />
          <div>
            <Eyebrow>{state.user?.name || state.user?.email}</Eyebrow>
            <TitleRow>
              <Title>{open.title}</Title>
              <PhaseTags w={open} t={t} />
            </TitleRow>
          </div>
        </HeadRow>
        <HeadBody>
          {open.workshopGoal && <Goal>{open.workshopGoal}</Goal>}
          {phaseOf(open) === 'upcoming' && open.event?.startsAt && (() => {
            const left = countdownTo(open.event.startsAt, now);
            if (!left) return null;
            const text = left.days > 0
              ? t('me.countdown.days', left)
              : t('me.countdown.hours', left);
            return <Countdown><Hourglass size={15} /> {text}</Countdown>;
          })()}
        </HeadBody>
      </Head>

      {open.canEnter ? (
      <Enter disabled={entering} onClick={async () => {
        setEntering(true);
        try {
          const res = await fetch(API_ENDPOINTS.ME_WORKSHOP_ENTER(open.id), { method: 'POST', credentials: 'include' });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.url) throw new Error(data.error || `HTTP ${res.status}`);
          window.location.assign(data.url);
        } catch (err) {
          setEntering(false);
          setEnterError(err.message);
        }
      }}>
        {t('me.enterWorkshop')} <ArrowRight size={16} />
      </Enter>
      ) : (
        phaseOf(open) !== 'past' && <Muted>{t(open.instanceType === 'live' ? 'me.enterOnSite' : 'me.enterLater')}</Muted>
      )}
      {enterError && <Muted>{enterError}</Muted>}

      {open.facilitates && (
        <Facilitate onClick={() => navigate(`/admin/workshop?instance=${open.id}`)}>
          {t('me.openFacilitator')} <ArrowRight size={16} />
        </Facilitate>
      )}

      <TabBar role="tablist">
        <Tab role="tab" aria-selected={tab === 'practical'} $active={tab === 'practical'}
             onClick={() => setTab('practical')}>{t('me.tabPractical')}</Tab>
        <Tab role="tab" aria-selected={tab === 'prework'} $active={tab === 'prework'}
             onClick={() => setTab('prework')}>
          {t('me.tabPreWork')}
          {open.preWorkLocked
            ? <Lock size={13} role="img" aria-label={t('me.preWorkClosedShort')} title={t('me.preWorkClosedShort')} style={{ marginLeft: '0.4rem', verticalAlign: '-1px', color: '#7b8794' }} />
            : todo[open.id] && <Dot role="img" aria-label={t('me.preWorkTodo')} title={t('me.preWorkTodo')} />}
        </Tab>
        {open.homework && (
          <Tab role="tab" aria-selected={tab === 'homework'} $active={tab === 'homework'}
               onClick={() => setTab('homework')}>
            {t('me.tabHomework')}
            {homeworkTodo[open.id] && <Dot role="img" aria-label={t('me.homeworkTodo')} title={t('me.homeworkTodo')} />}
          </Tab>
        )}
      </TabBar>

      {tab === 'homework' && open.homework ? (
        <HomeworkList instanceId={open.id} onTodoChange={(v) => setHomeworkTodoFor(open.id, v)} />
      ) : tab === 'prework' ? (
        (open.preWork || []).length || open.kuusiKenttaa ? (
          <PreWorkList instanceId={open.id} tasks={open.preWork || []} status={open.status} locked={!!open.preWorkLocked} kuusiKenttaa={!!open.kuusiKenttaa} selfAssessment={open.selfAssessment !== false} onTodoChange={(v) => setTodoFor(open.id, v)} />
        ) : (
          <Muted>{t('me.noPreWork')}</Muted>
        )
      ) : (
      <>
      {event ? (
        <Card>
          {when && <Row><Calendar size={17} /><div><SectionTitle>{t('me.when')}</SectionTitle><div>{when}</div></div></Row>}
          {(event.location || event.address) && (
            <Row>
              <MapPin size={17} />
              <div>
                <SectionTitle>{t('me.where')}</SectionTitle>
                <div>{event.location}</div>
                <div>{event.address}</div>
                {event.mapLink && (
                  <Link href={event.mapLink} target="_blank" rel="noreferrer">
                    {t('me.directions')} <ExternalLink size={14} />
                  </Link>
                )}
                <TransitLines transit={event.transit} />
              </div>
            </Row>
          )}
          <VenueMap place={event} />
          {(event.contactEmail || event.organiserEmail) && (
            <Row>
              <Mail size={17} />
              <div>
                <SectionTitle>{t('me.contact')}</SectionTitle>
                {event.organiserEmail && (
                  <div>
                    <Link href={`mailto:${event.organiserEmail}`}>
                      {event.organiserName || event.organiserEmail}
                    </Link>
                  </div>
                )}
                {event.contactEmail && event.contactEmail !== event.organiserEmail && (
                  <div><Link href={`mailto:${event.contactEmail}`}>{event.contactEmail}</Link></div>
                )}
                {event.attendingCount != null && (
                  <Muted>{t('me.attendingCount', { count: event.attendingCount })}</Muted>
                )}
              </div>
            </Row>
          )}
        </Card>
      ) : (
        <Card>
          <Row><Calendar size={17} /><div><SectionTitle>{t('me.when')}</SectionTitle><div>{open.workshopDate || t('me.dateLater')}</div></div></Row>
          <Row>
            <MapPin size={17} />
            <div>
              <SectionTitle>{t('me.where')}</SectionTitle>
              {open.instanceType === 'online'
                ? <div>{t('me.online')}</div>
                : open.place?.kind || open.place?.location || open.place?.address
                  ? <>
                      {/* The Kuutio room comes from the backend by kind; the page names it. */}
                      <div>{open.place.kind === 'kuutioRoom' ? t('me.kuutioRoom') : open.place.location}</div>
                      <div>{open.place.address}</div>
                      {open.place.mapLink && <Link href={open.place.mapLink} target="_blank" rel="noreferrer">{t('me.directions')} <ExternalLink size={14} /></Link>}
                      <TransitLines transit={open.place.transit} />
                    </>
                  : <div>{t('me.placeLater')}</div>}
            </div>
          </Row>
          <VenueMap place={open.place} />
          {open.contact?.email && (
            <Row><Mail size={17} /><div><SectionTitle>{t('me.contact')}</SectionTitle><div><Link href={`mailto:${open.contact.email}`}>{open.contact.name || open.contact.email}</Link></div></div></Row>
          )}
          <Muted>{open.unavailable ? t('me.detailsUnavailable') : t('me.noEventInfo')}</Muted>
        </Card>
      )}

      {/* Hyvä tietää: the organiser's notes from Lyyti, then the house's own
          lines — Chatham House, the devices (a live Kuutio only: there are
          none online), and how to come dressed. */}
      <Card>
        <SectionTitle><Info size={15} /> {t('me.goodToKnow')}</SectionTitle>
        <NoteList>
          {(event?.notes || []).map((note, i) => (
            <NoteItem key={i}>
              {note.heading && <strong>{note.heading}</strong>}
              {note.lines.map((line, j) => <div key={j}>{linkify(line)}</div>)}
            </NoteItem>
          ))}
          <NoteItem>
            <strong>{t('me.chatham.title')}</strong>
            <div>{t('me.chatham.body')}</div>
          </NoteItem>
          {open.instanceType !== 'online' && (
            <NoteItem>
              <strong>{t('me.devices.title')}</strong>
              <div>{t('me.devices.body')}</div>
            </NoteItem>
          )}
          <NoteItem>
            <strong>{t('me.comfort.title')}</strong>
            <div>{t('me.comfort.body')}</div>
          </NoteItem>
        </NoteList>
      </Card>

      <Card>
        <SectionTitle><BookOpen size={15} /> {open.reading?.title || t('me.reading.title')}</SectionTitle>
        <div>{open.reading?.body || t('me.reading.body')}</div>
        <Link href={open.reading?.url || t('me.reading.url')} target="_blank" rel="noreferrer">
          {open.reading?.linkText || t('me.reading.linkText')} <ExternalLink size={14} />
        </Link>
      </Card>

      <Card>
        <SectionTitle><Box size={15} /> {t('me.about.title')}</SectionTitle>
        {(open.about || t('me.about.lines')).map((line, i) => <div key={i}>{linkify(line)}</div>)}
      </Card>

      {you ? (
        <Card>
          <SectionTitle>{t(you.forParticipant ? 'me.theirDetails' : 'me.yourDetails')}</SectionTitle>
          <Muted>{you.forParticipant
            ? t('me.assistantIntro', { name: [you.forParticipant.firstName, you.forParticipant.lastName].filter(Boolean).join(' ') })
            : t('me.detailsIntro')}</Muted>
          <Dl>
            <dt>{t('me.organisation')}</dt><dd>{you.organisation || t('me.notGiven')}</dd>
            <dt>{t('me.title')}</dt><dd>{you.title || t('me.notGiven')}</dd>
            <dt>{t('me.allergies')}</dt><dd>{you.allergies || t('me.notGiven')}</dd>
            <dt>{t('me.diet')}</dt><dd>{you.diet || t('me.notGiven')}</dd>
            {you.phone && (<><dt>{t('me.phone')}</dt><dd>{you.phone}</dd></>)}
            {you.assistantEmail && (
              <><dt>{t('me.assistant')}</dt>
                <dd>{[you.assistantName, you.assistantEmail].filter(Boolean).join(' · ')}</dd></>
            )}
          </Dl>
          {you.selfServiceUrl && (
            <Link href={you.selfServiceUrl} target="_blank" rel="noreferrer">
              {t(you.forParticipant ? 'me.editTheirs' : 'me.editInLyyti')} <ExternalLink size={14} />
            </Link>
          )}
        </Card>
      ) : (
        <Muted>{t('me.noRegistration')}</Muted>
      )}
      </>
      )}
      <MeDatasets />
    </Column></Page>
  );
};

export default MeView;
